import { type ReactNode, useMemo } from "react";
import { type SubstepType } from "~/Configurators/Linear/dtos";
import type { CsticId, LinearCsticValue } from "~/Configurators/Linear/models";
import { useCsticSelectProps } from "~/Configurators/Shaft/components/useCsticSelectProps";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";
import LanguageAwareExternalContent from "~/Configurators/Linear/components/LanguageAwareExternalContent";
import type { DropdownEntry } from "~/common/types";

interface Props {
  csticId: CsticId;
  substepType: SubstepType;
  csticsToNull?: string[];
  csticsToSet?: Array<CsticToSet | Resolver>;
  disabled?: boolean;
  allowedValues?: string[];
  label?: string;
  helperLabel?: ReactNode;
  renderValue?: (value: LinearCsticValue, index: number) => ReactNode;
  shrink?: boolean;
  useCsticLabel?: boolean;
}

type CsticToSet =
  | {
      key: string;
      value: string | null;
    }
  | undefined;

type Resolver = (value: string | null) => CsticToSet;

export const SimpleCsticSelectField = ({
  label,
  helperLabel,
  csticId,
  allowedValues,
  substepType,
  renderValue = DEFAULT_RENDER_VALUE,
  csticsToNull,
  csticsToSet,
  disabled: externalDisabled,
  shrink,
  useCsticLabel,
}: Props) => {
  const { selectProps, csticValues } = useCsticSelectProps({
    csticId,
    substep: substepType,
    allowedValues,
    csticsToNull,
    csticsToSet,
    disabled: externalDisabled,
    useCsticLabel,
  });

  const values: DropdownEntry<string>[] = useMemo(
    () =>
      csticValues.map((value, index) => {
        return { value: value.value, label: renderValue(value, index) };
      }),
    [csticValues, renderValue],
  );

  return (
    <SimpleSelect
      label={<LanguageAwareExternalContent content={label} loadedClassname={"scha__lc__external-label"} />}
      helperText={helperLabel}
      fullWidth={true}
      values={values}
      shrink={shrink}
      {...selectProps}
      onChange={(e) => selectProps.onChange(e.target.value as string)}
    />
  );
};

const DEFAULT_RENDER_VALUE = (value: LinearCsticValue) => value.label;
